import {
    Controller,
    type Control,
    type FieldValues,
    type Path
} from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useValidationMessage } from '../i18n/validationMessages';
import { useFormReadOnly } from './formReadOnly';

/**
 * A date input bound to a react-hook-form field that stores a 'YYYY-MM-DD'
 * string (empty = unset). MUI X DatePicker, not the native date input, so the
 * format is always German (DD.MM.YYYY) regardless of the UI language; the
 * picker adapter locale is pinned to 'de' (see main.tsx / utils/formatDate).
 */
export default function DateStringField<T extends FieldValues>({
    control,
    name,
    label,
    disableFuture = false,
    disableSundays = false,
    helperText,
    readOnly
}: {
    control: Control<T>;
    name: Path<T>;
    label: string;
    disableFuture?: boolean;
    /** Opt-in: grey out Sundays in the calendar (e.g. no work on Sundays). */
    disableSundays?: boolean;
    /** Shown under the field while it has no error. */
    helperText?: string;
    /**
     * Shown but not editable — see formFields/readOnlySheet. Defaults to the
     * surrounding form's (see formReadOnly).
     */
    readOnly?: boolean;
}) {
    const validationMessage = useValidationMessage();
    const formReadOnly = useFormReadOnly();
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <DatePicker
                    label={label}
                    readOnly={readOnly ?? formReadOnly}
                    disableFuture={disableFuture}
                    shouldDisableDate={
                        disableSundays
                            ? (dateValue) => dayjs(dateValue).day() === 0
                            : undefined
                    }
                    value={field.value ? dayjs(field.value as string) : null}
                    onChange={(next) =>
                        field.onChange(
                            next && next.isValid()
                                ? next.format('YYYY-MM-DD')
                                : ''
                        )
                    }
                    slotProps={{
                        textField: {
                            inputRef: field.ref,
                            /**
                             * The picker's field doesn't inherit the
                             * MuiTextField filled default.
                             */
                            variant: 'filled',
                            fullWidth: true,
                            onBlur: field.onBlur,
                            error: !!fieldState.error,
                            helperText: fieldState.error
                                ? validationMessage(fieldState.error.message)
                                : helperText
                        }
                    }}
                />
            )}
        />
    );
}
